/**
 * Client-generated ids for play events (CLAUDE.md invariant 4: every device write is
 * idempotent, deduped on `(device_id, event_id)`; assume duplicate delivery always).
 * UUIDv7 to match the rest of the platform's keys, so an event id sorts by the time
 * the device recorded it.
 */

export type IdFactory = () => string;

export function createUuidV7Factory(options: {
  readonly now: () => number;
  readonly randomBytes?: (length: number) => Uint8Array;
}): IdFactory {
  const randomBytes = options.randomBytes ?? defaultRandomBytes;

  return () => {
    const bytes = randomBytes(16);
    const timestamp = Math.max(0, Math.floor(options.now()));

    // 48-bit big-endian millisecond timestamp.
    bytes[0] = Math.floor(timestamp / 2 ** 40) & 0xff;
    bytes[1] = Math.floor(timestamp / 2 ** 32) & 0xff;
    bytes[2] = Math.floor(timestamp / 2 ** 24) & 0xff;
    bytes[3] = Math.floor(timestamp / 2 ** 16) & 0xff;
    bytes[4] = Math.floor(timestamp / 2 ** 8) & 0xff;
    bytes[5] = timestamp & 0xff;

    bytes[6] = ((bytes[6] as number) & 0x0f) | 0x70; // version 7
    bytes[8] = ((bytes[8] as number) & 0x3f) | 0x80; // RFC 4122 variant

    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32),
    ].join('-');
  };
}

function defaultRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const webCrypto = globalThis.crypto;
  if (webCrypto !== undefined && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
    return bytes;
  }
  // A WebView with no crypto still needs unique-enough ids; the server dedupes on
  // (device_id, event_id), so a collision across devices is harmless.
  for (let index = 0; index < length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }
  return bytes;
}
